import { useEffect, useRef, useState } from 'react';
import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import MarkEmailReadRounded from '@mui/icons-material/MarkEmailReadRounded';
import UnsubscribeRounded from '@mui/icons-material/UnsubscribeRounded';
import { useSearchParams } from 'react-router';
import { leOfertasPorLink, mudaOfertasPorLink } from '@/features/notas/service';

/**
 * Link dos e-mails (sem login). Com "acao=sair" o próprio clique no e-mail já desliga (um clique só);
 * a página mostra a situação e deixa voltar atrás com outro clique.
 */
export function PreferenciasPage() {
  const [params] = useSearchParams();
  const ra = params.get('ra') ?? '';
  const t = params.get('t') ?? '';
  const sair = params.get('acao') === 'sair';
  const [estado, setEstado] = useState<{ nome: string; ofertas: boolean } | null>(null);
  const [erro, setErro] = useState<string | null>(ra && t ? null : 'Link incompleto.');
  const [salvando, setSalvando] = useState(false);
  const feito = useRef(false);

  useEffect(() => {
    if (feito.current || !ra || !t) return;
    feito.current = true;
    (async () => {
      try {
        const atual = await leOfertasPorLink(ra, t);
        if (sair && atual.ofertas) await mudaOfertasPorLink(ra, t, false);
        setEstado({ nome: atual.nome, ofertas: sair ? false : atual.ofertas });
      } catch {
        setErro('Este link não é válido. Entre no painel para mudar a sua preferência.');
      }
    })();
  }, [ra, t, sair]);

  const muda = async (aceita: boolean) => {
    setSalvando(true);
    try {
      await mudaOfertasPorLink(ra, t, aceita);
      setEstado((e) => (e ? { ...e, ofertas: aceita } : e));
    } finally {
      setSalvando(false);
    }
  };

  if (erro) {
    return (
      <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
        {erro}
      </Typography>
    );
  }
  if (!estado) return <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />;

  return (
    <Stack spacing={2.5} sx={{ alignItems: 'center', textAlign: 'center', py: 2 }}>
      {estado.ofertas ? (
        <MarkEmailReadRounded color="primary" sx={{ fontSize: 56 }} />
      ) : (
        <UnsubscribeRounded color="action" sx={{ fontSize: 56 }} />
      )}
      <Typography variant="h5" component="h1">
        {estado.ofertas ? `${estado.nome}, você recebe as novidades` : `Pronto, ${estado.nome}`}
      </Typography>
      <Typography color="text.secondary">
        {estado.ofertas
          ? 'Você está recebendo as novidades do Prof. Bossini: notícias de informática, cursos, oportunidades e avisos de notas.'
          : 'Você não vai mais receber as novidades do Prof. Bossini. Avisos das disciplinas (como a confirmação do RA) continuam chegando.'}
      </Typography>
      <Button
        variant={estado.ofertas ? 'outlined' : 'text'}
        disabled={salvando}
        onClick={() => void muda(!estado.ofertas)}
      >
        {estado.ofertas ? 'Não quero mais receber' : 'Mudei de ideia: quero receber'}
      </Button>
    </Stack>
  );
}
